import { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Robot, ArrowRight, Copy, Lightning } from "@phosphor-icons/react";

const TONES = ["energetic", "cinematic", "stoic", "mystery", "documentary"];

export default function ScriptGenerator() {
    const [topic, setTopic] = useState("");
    const [tone, setTone] = useState("energetic");
    const [loading, setLoading] = useState(false);
    const [script, setScript] = useState("");

    const generate = async (e) => {
        e?.preventDefault();
        if (!topic.trim()) {
            toast.error("Enter a topic first");
            return;
        }
        setLoading(true);
        setScript("");
        try {
            const r = await api.post("/script/generate", { topic, tone });
            setScript(r.data.script);
            toast.success("SCRIPT GENERATED // +25 PTS");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Generation failed");
        } finally {
            setLoading(false);
        }
    };

    const copyScript = () => {
        navigator.clipboard.writeText(script);
        toast.success("Script copied to clipboard");
    };

    return (
        <section data-testid="script-generator-section" className="bg-[#0A0A0A] border border-white/10 relative overflow-hidden h-full flex flex-col">
            <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="relative">
                        <Robot size={22} className="text-electric-blue" weight="duotone" />
                        <span className="absolute -top-1 -right-1 w-2 h-2 bg-electric-blue rounded-full animate-pulse" />
                    </div>
                    <div>
                        <p className="label-mono text-electric-blue">NEXUS SCRIPT ENGINE</p>
                        <h2 className="font-heading font-bold text-lg">Gemini 3 Flash · 60-Sec Generator</h2>
                    </div>
                </div>
                <span className="hidden md:flex items-center gap-1 label-mono text-[10px] text-emerald-400">
                    <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse" /> ONLINE
                </span>
            </div>

            <form onSubmit={generate} className="p-6 space-y-4">
                <div>
                    <label className="label-mono mb-2 block">TOPIC INPUT</label>
                    <input
                        type="text"
                        data-testid="script-topic-input"
                        value={topic}
                        onChange={(e) => setTopic(e.target.value)}
                        placeholder="e.g. The lost technology of the Antikythera mechanism"
                        className="w-full bg-[#060808] border border-white/10 px-4 py-3 text-white outline-none focus:border-electric-blue focus:ring-1 focus:ring-electric-blue transition-all font-mono text-sm"
                    />
                </div>

                <div>
                    <label className="label-mono mb-2 block">TONE</label>
                    <div className="flex flex-wrap gap-2">
                        {TONES.map((t) => (
                            <button
                                type="button"
                                key={t}
                                data-testid={`script-tone-${t}`}
                                onClick={() => setTone(t)}
                                className={`px-3 py-1.5 text-xs uppercase tracking-wider border transition-all ${
                                    tone === t
                                        ? "bg-electric-blue border-electric-blue text-white"
                                        : "bg-transparent border-white/10 text-muted-foreground hover:border-electric-blue/50 hover:text-white"
                                }`}
                            >
                                {t}
                            </button>
                        ))}
                    </div>
                </div>

                <button
                    type="submit"
                    disabled={loading}
                    data-testid="script-generate-btn"
                    className="w-full bg-electric-blue hover:bg-electric-blue-dark disabled:opacity-50 text-white font-bold py-3 text-sm tracking-wider uppercase transition-all hover:shadow-[0_0_24px_rgba(0,123,255,0.5)] flex items-center justify-center gap-2"
                >
                    {loading ? (
                        <>
                            <Lightning size={16} weight="fill" className="animate-pulse" />
                            ENGINE RUNNING...
                        </>
                    ) : (
                        <>
                            <Robot size={16} weight="fill" />
                            GENERATE 60-SEC SCRIPT
                            <ArrowRight size={16} weight="bold" />
                        </>
                    )}
                </button>
            </form>

            {/* Terminal Output */}
            <div className="px-6 pb-6">
                <div className="terminal-window relative p-5 min-h-[260px] max-h-[420px] overflow-y-auto scrollbar-thin">
                    <div className="absolute top-3 right-3 flex items-center gap-2">
                        {script && (
                            <button
                                data-testid="script-copy-btn"
                                onClick={copyScript}
                                className="text-muted-foreground hover:text-electric-blue transition-colors"
                                aria-label="Copy script"
                            >
                                <Copy size={16} weight="bold" />
                            </button>
                        )}
                    </div>
                    <div className="flex gap-2 mb-3">
                        <span className="w-2 h-2 rounded-full bg-red-500/70" />
                        <span className="w-2 h-2 rounded-full bg-yellow-500/70" />
                        <span className="w-2 h-2 rounded-full bg-emerald-500/70" />
                        <span className="ml-2 label-mono text-[10px] text-muted-foreground">nexus.script.engine</span>
                    </div>
                    {!script && !loading && (
                        <p data-testid="script-empty-state" className="font-mono text-xs text-muted-foreground leading-relaxed">
                            <span className="text-electric-blue">$</span> Awaiting topic input...
                            <br />
                            <span className="text-electric-blue">$</span> Enter a topic above and hit generate.
                            <br />
                            <span className="text-electric-blue">$</span> Output: 4-act 60-second script (HOOK → BUILD-UP → REVELATION → CTA).
                        </p>
                    )}
                    {loading && (
                        <p className="font-mono text-xs text-electric-blue animate-pulse">
                            <span>$</span> Generating viral script for "{topic}"...
                        </p>
                    )}
                    {script && (
                        <pre data-testid="script-output" className="font-mono text-sm text-foreground whitespace-pre-wrap leading-relaxed">
                            {script}
                        </pre>
                    )}
                </div>
            </div>
        </section>
    );
}
